import { FormValidation } from "@rjsf/utils";
import moment from "moment";

import {
  CreateGMWEventTask,
  CreateGMWTask,
  EditGMWEventTask,
  EditGMWTask,
} from "@nens/brostar/uploadapi-client";

import { parseNumber } from "@/helpers/parse-number";

/**
 * Unified custom validation function for GMW Event and GMW (Monitoring Well) forms.
 * Validates field ranges and formats that cannot be expressed in JSON Schema.
 */
export const validateGmwFormUnified = (
  formData: CreateGMWEventTask | EditGMWEventTask | CreateGMWTask | EditGMWTask | undefined,
  errors: FormValidation<CreateGMWEventTask | EditGMWEventTask | CreateGMWTask | EditGMWTask>
): FormValidation<CreateGMWEventTask | EditGMWEventTask | CreateGMWTask | EditGMWTask> => {
  if (!formData?.sourcedocumentData) return errors;
  const { sourcedocumentData } = formData;
  const sdErrors: Record<string, unknown> = errors.sourcedocumentData ?? {};

  // Helper to safely add field error using bracket notation with string keys
  // Supports nested paths like "monitoringTubes.0.tubeNumber"
  const addFieldError = (field: string, message: string) => {
    const parts = field.split(".");
    let current: unknown = sdErrors;
    for (const part of parts) {
      if (!current || typeof current !== "object") return;
      current = current[part as keyof typeof current];
    }
    if (current && typeof current === "object" && "addError" in current) {
      const errorObj = current as { addError: (msg: string) => void };
      if (typeof errorObj.addError === "function") {
        errorObj.addError(message);
      }
    }
  };

  // deliveredLocation: must contain x and y coordinates
  if ("deliveredLocation" in sourcedocumentData && sourcedocumentData.deliveredLocation) {
    const coords = sourcedocumentData.deliveredLocation.trim().split(/\s+/);
    if (
      coords.length !== 2 ||
      Number.isNaN(parseNumber(coords[0])) ||
      Number.isNaN(parseNumber(coords[1]))
    ) {
      addFieldError("deliveredLocation", "Moet twee coördinaten bevatten (x y)");
    }
  }

  // offset: must be a valid number
  if (
    "offset" in sourcedocumentData &&
    sourcedocumentData.offset !== undefined &&
    sourcedocumentData.offset !== null
  ) {
    const value = parseNumber(sourcedocumentData.offset);
    if (Number.isNaN(value)) {
      addFieldError("offset", "Moet een geldig getal zijn");
    }
  }

  // groundLevelPosition: -30 to 325 (string field validated as number)
  if (
    "groundLevelPosition" in sourcedocumentData &&
    sourcedocumentData.groundLevelPosition !== undefined
  ) {
    const value = parseNumber(sourcedocumentData.groundLevelPosition);
    if (!Number.isNaN(value)) {
      if (value < -30) {
        addFieldError("groundLevelPosition", "Kan niet kleiner zijn dan -30");
      } else if (value > 325) {
        addFieldError("groundLevelPosition", "Kan niet groter zijn dan 325");
      }
    }
  }

  // wellConstructionDate: not in the future
  if (
    "wellConstructionDate" in sourcedocumentData &&
    sourcedocumentData.wellConstructionDate !== undefined &&
    sourcedocumentData.wellConstructionDate !== null
  ) {
    if (moment().diff(sourcedocumentData.wellConstructionDate) < 0) {
      addFieldError("wellConstructionDate", "Datum mag niet in de toekomst zijn");
    }
  }

  // eventDate: not in the future
  if (
    "eventDate" in sourcedocumentData &&
    sourcedocumentData.eventDate !== undefined &&
    sourcedocumentData.eventDate !== null
  ) {
    if (moment().diff(sourcedocumentData.eventDate) < 0) {
      addFieldError("eventDate", "Datum mag niet in de toekomst zijn");
    }
  }

  // owner: 8 digits (KVK number)
  if ("owner" in sourcedocumentData && sourcedocumentData.owner !== undefined) {
    const value = String(sourcedocumentData.owner);
    if (value && !/^\d{8}$/.test(value)) {
      addFieldError("owner", "Een KVK-nummer moet precies 8 cijfers lang zijn");
    }
  }

  // maintenanceResponsibleParty: 8 digits (KVK number)
  if (
    "maintenanceResponsibleParty" in sourcedocumentData &&
    sourcedocumentData.maintenanceResponsibleParty !== undefined
  ) {
    const value = String(sourcedocumentData.maintenanceResponsibleParty);
    if (value && !/^\d{8}$/.test(value)) {
      addFieldError(
        "maintenanceResponsibleParty",
        "Een KVK-nummer moet precies 8 cijfers lang zijn"
      );
    }
  }

  // insertedPartLength: 0 to 500
  if (
    "insertedPartLength" in sourcedocumentData &&
    sourcedocumentData.insertedPartLength !== undefined
  ) {
    const value = parseNumber(sourcedocumentData.insertedPartLength);
    if (!Number.isNaN(value)) {
      if (value < 0) {
        addFieldError("insertedPartLength", "Kan niet kleiner zijn dan 0");
      } else if (value > 500) {
        addFieldError("insertedPartLength", "Kan niet groter zijn dan 500");
      }
    }
  }

  // Validate monitoring tubes
  if (
    "monitoringTubes" in sourcedocumentData &&
    Array.isArray(sourcedocumentData.monitoringTubes)
  ) {
    sourcedocumentData.monitoringTubes.forEach((tube, index) => {
      // tubeNumber: 0 to 1000 (string field validated as number)
      if ("tubeNumber" in tube && tube.tubeNumber !== undefined) {
        const value = parseNumber(tube.tubeNumber);
        if (!Number.isNaN(value)) {
          if (value < 0) {
            addFieldError(`monitoringTubes.${index}.tubeNumber`, "Kan niet kleiner zijn dan 0");
          } else if (value > 1000) {
            addFieldError(`monitoringTubes.${index}.tubeNumber`, "Kan niet groter zijn dan 1000");
          }
        }
      }

      // tubeTopPosition: -10 to 325
      if ("tubeTopPosition" in tube && tube.tubeTopPosition !== undefined) {
        const value = parseNumber(tube.tubeTopPosition);
        if (!Number.isNaN(value)) {
          if (value < -10) {
            addFieldError(
              `monitoringTubes.${index}.tubeTopPosition`,
              "Kan niet kleiner zijn dan -10"
            );
          } else if (value > 325) {
            addFieldError(
              `monitoringTubes.${index}.tubeTopPosition`,
              "Kan niet groter zijn dan 325"
            );
          }
        }
      }

      // screenLengtg: 0 or 0.002 to 1000 (string field validated as number)
      if ("screenLength" in tube && tube.screenLength !== undefined) {
        const value = parseNumber(tube.screenLength);
        if (!Number.isNaN(value)) {
          if (value === 0 && tube.tubeType === "filterlozeBuis") {
            // Allow 0 for filterlozeBuis
          } else if (value < 0.02) {
            addFieldError(
              `monitoringTubes.${index}.screenLength`,
              "Kan niet kleiner zijn dan 0.02"
            );
          } else if (value > 1000) {
            addFieldError(`monitoringTubes.${index}.screenLength`, "Kan niet groter zijn dan 1000");
          }
        }
      }

      // sedimentSumpLength > 0.05
      if ("sedimentSumpLength" in tube && tube.sedimentSumpPresent === "ja") {
        const value = parseNumber(tube.sedimentSumpLength);
        if (!Number.isNaN(value)) {
          if (value === 0 && tube.tubeType === "filterlozeBuis") {
            // Allow 0 for filterlozeBuis
          } else if (value < 0.05) {
            addFieldError(
              `monitoringTubes.${index}.sedimentSumpLength`,
              "Kan niet kleiner zijn dan 0.05"
            );
          } else if (value > 1000) {
            addFieldError(
              `monitoringTubes.${index}.sedimentSumpLength`,
              "Kan niet groter zijn dan 1000"
            );
          }
        }
      }

      // tubeTopDiameter: 3 to 1000 (float)
      if ("tubeTopDiameter" in tube && tube.tubeTopDiameter !== undefined) {
        const floatValue = parseNumber(tube.tubeTopDiameter);
        if (!Number.isNaN(floatValue)) {
          if (floatValue < 3) {
            addFieldError(
              `monitoringTubes.${index}.tubeTopDiameter`,
              "Kan niet kleiner zijn dan 3"
            );
          } else if (floatValue > 1000) {
            addFieldError(
              `monitoringTubes.${index}.tubeTopDiameter`,
              "Kan niet groter zijn dan 1000"
            );
          }
        }
      }
    });
  }

  // Note: Range validations for true numeric fields (tubeTopDiameter, tubeTopPosition,
  // numberOfGeoOhmCables, screenLength, sedimentSumpLength, electrodePosition, insertedPartLength,
  // insertedPartDiameter, plainTubePartLength) are handled by minimum/maximum in gmw-schema-library.ts

  return errors;
};
